/**
 * PricingModal — Free vs Pro membership. Starts a Stripe Checkout session
 * when the user clicks a pass.
 *
 * Rendered through a portal on <body>: the Creator header uses
 * backdrop-blur, which turns it into the containing block for any
 * `position: fixed` child — the modal used to be centred on the 60-px
 * header and extend above the screen with the close button off-screen.
 *
 * Flow: sign-in → POST /api/billing/checkout {package_id, origin_url} →
 * redirect to Stripe → /billing/success (BillingSuccess.jsx).
 */

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, Check, X, Zap, Crown, LogIn, CalendarOff, FolderCheck, HardDrive, Cloud, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import useAuth from "@/lib/useAuth";

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURES = {
  free: [
    "Create songs, record vocals, separate stems and master",
    "All 64 mixer channels and real-time effects",
    "Lyrics writer",
    "Standard daily limits on AI songs, stems and voice",
    "Public song share pages",
    "Save to this browser — or sign in (free) to save to your account",
  ],
  pro: [
    "Everything in Free, plus:",
    "Higher daily limits on AI songs, stems and voice",
    "Pro plugin library and premium voice models",
    "Priority in the generation queue",
    "Pro badge on your account",
  ],
};

const PACKAGES = [
  {
    id: "pro_monthly",
    label: "30-Day Pass",
    price: "$9.99",
    currency: "USD",
    period: "one-time · 30 days",
    note: "Does not auto-renew",
    accent: "from-fuchsia-500 to-rose-500",
  },
  {
    id: "pro_yearly",
    label: "1-Year Pass",
    price: "$99.90",
    currency: "USD",
    period: "one-time · 365 days",
    note: "Save 16% · Does not auto-renew",
    accent: "from-emerald-500 to-sky-500",
    badge: "BEST VALUE",
  },
];

const FACTS = [
  { icon: CalendarOff, title: "No automatic renewal", body: "A pass is a one-time payment in US dollars. Nothing is charged again unless you buy another pass. Buying while a pass is active adds days to it." },
  { icon: FolderCheck, title: "When a pass ends", body: "Your projects stay saved, playable and downloadable. Only the Pro tools lock until you renew — nothing is deleted." },
  { icon: HardDrive, title: "Browser storage", body: "Without signing in, your songs are tied to this browser on this device. Clearing browser data or switching devices means they won't appear." },
  { icon: Cloud, title: "Saved to your account", body: "Sign in (free) and your songs, recordings and stems save to your account, so you can open them on any device." },
];

export default function PricingModal({ open, onClose }) {
  const { user, signIn, refresh } = useAuth();
  const [busy, setBusy] = useState(null);
  const [plan, setPlan] = useState({ plan: "free", days_remaining: 0 });

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/billing/plan`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setPlan)
      .catch(() => { /* keep default free */ });
  }, [open]);

  // Esc to close + lock the page scroll behind the sheet.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);

  const isPro = useMemo(() => plan?.plan === "pro" && plan?.days_remaining > 0, [plan]);

  const handleUpgrade = async (pkgId) => {
    if (!user) {
      toast.message("Sign in first", { description: "Pro passes are bound to your account." });
      signIn();
      return;
    }
    setBusy(pkgId);
    try {
      const res = await fetch(`${API}/api/billing/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ package_id: pkgId, origin_url: window.location.origin }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const body = await res.json();
      if (!body?.url) throw new Error("No checkout URL");
      window.location.href = body.url;
    } catch (e) {
      console.error("[billing] checkout failed:", e);
      toast.error("Could not start checkout", { description: e.message || "Try again in a moment." });
      setBusy(null);
    }
  };

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="pricing-backdrop"
        className="fixed inset-0 z-[90] overflow-y-auto overscroll-contain bg-black/80 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="pricing-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pricing-title"
      >
        <div className="flex min-h-full items-start justify-center p-3 sm:items-center sm:p-6">
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
            className="relative flex max-h-[calc(100dvh-1.5rem)] w-full max-w-[1040px] flex-col overflow-hidden rounded-3xl border border-white/[0.12] bg-gradient-to-br from-[#0b0d1a] to-[#06070f] shadow-[0_24px_64px_-16px_rgba(0,0,0,0.75)] sm:max-h-[calc(100dvh-3rem)]"
            data-testid="pricing-modal"
          >
            {/* Header — stays visible while the body scrolls */}
            <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-white/[0.08] bg-[#0b0d1a]/95 px-5 py-4 backdrop-blur-xl sm:px-7">
              <div className="flex items-center gap-3">
                <Crown className="h-5 w-5 text-amber-300" />
                <div>
                  <h2 id="pricing-title" className="font-display text-[20px] font-semibold tracking-tight text-white sm:text-[22px]">Pro membership</h2>
                  <p className="text-[13px] text-white/75">One-time passes in USD · no automatic renewal</p>
                </div>
              </div>
              <button
                type="button"
                data-testid="pricing-close-btn"
                onClick={onClose}
                className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-white/15 bg-white/[0.06] text-white transition hover:bg-white/[0.14]"
                aria-label="Close pricing"
              >
                <X className="h-5 w-5" />
              </button>
            </header>

            <div className="overflow-y-auto">
              {isPro && (
                <div data-testid="pricing-already-pro" className="border-b border-emerald-400/20 bg-emerald-500/[0.08] px-5 py-3 sm:px-7">
                  <p className="text-[14px] font-semibold text-emerald-100">
                    You're on Pro — {plan.days_remaining} day{plan.days_remaining === 1 ? "" : "s"} remaining
                    {plan.plan_expires_at ? ` (ends ${new Date(plan.plan_expires_at).toLocaleDateString()})` : ""}.
                  </p>
                  <p className="mt-1 text-[13px] text-emerald-100/80">
                    Buy any pass below to add days to your current window — it never resets what you already have.
                  </p>
                </div>
              )}

              <div className="grid gap-4 p-5 sm:p-7 lg:grid-cols-2">
                <section data-testid="pricing-free-card" className="rounded-2xl border border-white/[0.1] bg-white/[0.03] p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <Zap className="h-4 w-4 text-sky-300" />
                    <h3 className="font-display text-[16px] font-semibold uppercase tracking-wider text-white">Free</h3>
                    {!isPro && plan?.authenticated && (
                      <span data-testid="pricing-current-free" className="ml-auto rounded-full bg-sky-500/[0.18] px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-sky-100 ring-1 ring-sky-400/40">Current</span>
                    )}
                  </div>
                  <p className="mb-4 font-display text-[30px] font-semibold leading-none text-white">$0 <span className="text-[13px] font-normal uppercase tracking-wider text-white/70">USD · forever</span></p>
                  <ul className="space-y-2.5">
                    {FEATURES.free.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[14px] leading-snug text-white/85">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-sky-300" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </section>

                <section data-testid="pricing-pro-card" className="relative rounded-2xl border border-fuchsia-400/35 bg-gradient-to-br from-fuchsia-500/[0.1] to-amber-400/[0.06] p-5">
                  <div className="mb-3 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-fuchsia-300" />
                    <h3 className="font-display text-[16px] font-semibold uppercase tracking-wider text-white">Pro</h3>
                    {isPro && (
                      <span data-testid="pricing-current-pro" className="ml-auto rounded-full bg-emerald-500/[0.18] px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-100 ring-1 ring-emerald-400/40">Current</span>
                    )}
                  </div>
                  <ul className="mb-5 space-y-2.5">
                    {FEATURES.pro.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[14px] leading-snug text-white">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-fuchsia-300" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="space-y-2.5">
                    {PACKAGES.map((pkg) => (
                      <button
                        key={pkg.id}
                        type="button"
                        data-testid={`pricing-pkg-${pkg.id}`}
                        onClick={() => handleUpgrade(pkg.id)}
                        disabled={!!busy}
                        className={`group relative flex w-full items-center justify-between gap-3 rounded-xl border border-white/[0.1] bg-gradient-to-r ${pkg.accent} p-3.5 text-left transition hover:scale-[1.01] hover:ring-2 hover:ring-white/40 disabled:cursor-wait disabled:opacity-60`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-display text-[17px] font-semibold leading-tight text-white">{pkg.label}</p>
                            {pkg.badge && (
                              <span className="rounded-full bg-black/35 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-white ring-1 ring-white/20">{pkg.badge}</span>
                            )}
                          </div>
                          <p className="mt-0.5 text-[12.5px] text-white/90">{pkg.note}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-display text-[20px] font-semibold leading-none text-white">{pkg.price} <span className="text-[12px] font-medium">{pkg.currency}</span></p>
                          <p className="mt-1 text-[11.5px] uppercase tracking-wider text-white/90">{pkg.period}</p>
                        </div>
                        {busy === pkg.id && (
                          <span className="absolute inset-0 grid place-items-center rounded-xl bg-black/50 backdrop-blur-sm">
                            <span className="text-[12px] font-semibold uppercase tracking-[0.2em] text-white">Redirecting to Stripe…</span>
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  {!user && (
                    <button
                      type="button"
                      data-testid="pricing-signin-prompt"
                      onClick={() => signIn()}
                      className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-fuchsia-400/45 bg-fuchsia-500/[0.14] px-3 py-2.5 text-[13px] font-semibold uppercase tracking-wider text-fuchsia-50 transition hover:bg-fuchsia-500/[0.26]"
                    >
                      <LogIn className="h-4 w-4" />
                      Sign in to buy a pass
                    </button>
                  )}

                  <p className="mt-3 flex items-center justify-center gap-1.5 text-center text-[12px] text-white/70">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-300" /> Secure card payment by Stripe · prices in US dollars
                  </p>
                </section>
              </div>

              <section data-testid="pricing-facts" className="grid gap-3 border-t border-white/[0.08] px-5 py-5 sm:grid-cols-2 sm:px-7">
                {FACTS.map((f) => {
                  const Icon = f.icon;
                  return (
                    <div key={f.title} className="flex gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
                      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-300" />
                      <div>
                        <p className="text-[14px] font-semibold text-white">{f.title}</p>
                        <p className="mt-1 text-[13px] leading-relaxed text-white/75">{f.body}</p>
                      </div>
                    </div>
                  );
                })}
              </section>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.08] bg-black/30 px-5 py-4 text-[13px] text-white/75 sm:px-7">
                <span>
                  Questions about ownership, commercial use or privacy?{" "}
                  <Link to="/about" data-testid="pricing-about-link" onClick={onClose} className="font-semibold text-blue-200 underline-offset-2 hover:underline">Read Your Music &amp; Your Rights</Link>
                </span>
                <button
                  type="button"
                  data-testid="pricing-refresh"
                  onClick={() => { refresh?.(); toast.message("Plan refreshed"); }}
                  className="text-white/80 underline-offset-2 hover:text-white hover:underline"
                >
                  Refresh my plan
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
